/* eslint-disable complexity */
import React, { Dispatch, SetStateAction, useState } from 'react';
import { UpdateReport } from 'src/api';
import { Button, Checkbox, Tag } from 'src/components/atoms';
import { Modal } from 'src/components/atomsLegacy';
import { SwitchButton } from 'src/components/atomsLegacy/buttons/SwitchButton';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import {
  DefaultOption,
  FormInput,
  InformationBanner,
} from 'src/components/molecules';
import { UserType, useRelationshipTypeQuery } from 'src/graphql';
import { useSearchUsers } from 'src/hooks';
import { SYSTEM_PERMISSIONS } from 'src/permissionsTypes';
import { useTheme } from 'styled-components';
import { FormInputVariant, FormState, User } from 'src/types';
import { PillContainer } from '../ReportDetails/ReportDetails.styles';
import { SearchBox } from '../SearchBox';
import {
  AddUserText,
  AnonymousContainer,
  AnonymousText,
  BannerContainer,
  Body,
  ExplainerText,
  ExteralReporterContainer,
  Footer,
  Header,
  Padding,
  SubTitle,
  SwitchContainer,
  TextBox,
  TextInput,
  Title,
} from './AddUserModal.styles';

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  reportID: string;
  currentReport: UpdateReport | null;
  updateReport: (reportID: string, report: UpdateReport) => void;
  showOnlyCM: boolean;
  title: string;
  addUserText: string;
  alternateUserText?: string;
  subTitle: string;
  'data-testid': string;
  updateData: Function;
  buttonText: string;
  currentUser?: User;
  onlyAppUsers: boolean;
  allowUpdatingInternalUserRelationshipToOrg?: boolean;
}

const employeeOptions = [
  {
    value: 'Employee directory',
    dataTestId: 'add-internal-reporter',
  },
  { value: 'Manually', dataTestId: 'add-external-reporter' },
];

export const AddUserModal = ({
  open,
  setOpen,
  reportID,
  currentReport,
  updateReport,
  showOnlyCM,
  title,
  addUserText,
  subTitle,
  updateData,
  buttonText,
  currentUser,
  alternateUserText,
  onlyAppUsers: invitedAppUsersOnly,
  allowUpdatingInternalUserRelationshipToOrg,
  ...props
}: Props) => {
  const theme = useTheme();
  const { data: relationshipQueryType } = useRelationshipTypeQuery();

  const isExternal = () => {
    if (showOnlyCM) return false;
    return (
      Boolean(currentReport?.User?.eid) &&
      currentReport?.User?.email &&
      currentReport?.User?.email.length > 0
    );
  };

  const [internalUser, setIsInternalUser] = React.useState(!isExternal());
  const columns = ['Name', 'Role', 'Department', 'Country'];
  const setAnonymous = async () => {
    await updateReport(reportID, {
      ...currentReport,
      anonymous: !currentReport?.anonymous,
    });
  };

  const internalPerpetrators = currentReport?.perpetrators
    ?.filter((perpetrator) => !perpetrator.external)
    .map((perpetrator) => parseInt(perpetrator.id as string));

  const usersToExclude = [];
  if (currentReport?.assignee) {
    usersToExclude.push(currentReport.assignee);
  }

  if (internalPerpetrators) {
    usersToExclude.push(...internalPerpetrators);
  }

  if (currentReport?.User?.id) {
    usersToExclude.push(currentReport?.User?.id);
  }

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [relationshipToCompany, setRelationshipToCompany] = useState<{
    label: string;
    value: number;
  }>({
    label: '',
    value: 0,
  });

  React.useEffect(() => {
    if (!currentReport?.User?.eid) {
      // If name/email and it's external populate with that
      setName(currentReport?.User?.name ?? '');
      setEmail(currentReport?.User?.email ?? '');
    }
  }, [
    currentReport?.User?.eid,
    currentReport?.User?.email,
    currentReport?.User?.name,
  ]);

  React.useEffect(() => {
    searchUsers('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const currentUserIsReporter = currentReport?.user === currentUser?.id;

  const { selectedUser, rows, searchUsers, setSelectedUser } = useSearchUsers(
    columns,
    usersToExclude,
    invitedAppUsersOnly,
    showOnlyCM,
    showOnlyCM &&
      currentUser?.systemPermissions?.includes(
        SYSTEM_PERMISSIONS.ASSIGNABLE_TO_CASE_TEAM,
      ) &&
      currentUser?.systemPermissions?.includes(
        SYSTEM_PERMISSIONS.ASSIGNABLE_AS_CASE_LEAD,
      ) &&
      !currentUserIsReporter
      ? (currentUser as UserType)
      : null,
  );

  const addUserDisabled = () => {
    if (internalUser) {
      return false;
    }

    const reporterHasEID = !!currentReport?.User?.eid;
    const isNoEmail = !email;

    return reporterHasEID || isNoEmail;
  };

  const suffixText = selectedUser?.eid === currentUser?.eid ? ' (You)' : '';

  const selectedUserName =
    selectedUser && !currentUserIsReporter
      ? `${selectedUser?.name}${suffixText}`
      : 'Select employee';

  const userPromptCopy = () => {
    if (showOnlyCM) {
      if (!selectedUser) return alternateUserText;
      if (selectedUser?.eid === currentUser?.eid) {
        return `You are ${addUserText}`;
      }
      return `${selectedUserName} is ${addUserText}`;
    }
    return `${addUserText}`;
  };

  const selectedRelationshipToCompany =
    relationshipToCompany.value !== 0 && relationshipToCompany.label !== ''
      ? relationshipToCompany
      : null;

  const showWarning =
    selectedUser?.eid !== currentUser?.eid &&
    !currentUser?.systemPermissions?.includes('EDIT_ALL_CASES') &&
    showOnlyCM;

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      data-testid={`add-${props['data-testid']}-modal`}
    >
      <Header>
        <Title>{title}</Title>
        <SwitchContainer>
          <AddUserText>{userPromptCopy()}</AddUserText>
          {!showOnlyCM && (
            <SwitchButton
              name={`internal-${props['data-testid']}`}
              options={employeeOptions}
              setSelectedOption={(option) => {
                setIsInternalUser(option.value === employeeOptions[0].value);
                setRelationshipToCompany({ label: '', value: 0 });
                setSelectedUser(null);
                setName('');
                setEmail('');
              }}
              selectedOption={
                internalUser
                  ? employeeOptions[0].value
                  : employeeOptions[1].value
              }
            />
          )}
        </SwitchContainer>
        {showWarning && (
          <BannerContainer>
            <InformationBanner
              data-testid="add-user-access-warning"
              state={FormState.WARNING}
              title="Report access after submission"
              description="You will not have access to view or edit this report after submission if you're not the case manager assigned."
            />
          </BannerContainer>
        )}
      </Header>
      <Body>
        {showOnlyCM && (
          <PillContainer data-testid="add-user-pill-container">
            <Tag
              text={selectedUserName}
              background-color={theme.atlas.semantic.color.outline.default}
              onClick={() => setSelectedUser(null)}
              data-testid="clear-user-pill"
            />
          </PillContainer>
        )}
        <Padding />
        {internalUser ? (
          <>
            <SearchBox
              columnHeaders={[]}
              rows={rows}
              searchUsers={searchUsers}
              testLabel={'employee-search'}
              visible={open}
            />
            {!showOnlyCM &&
              allowUpdatingInternalUserRelationshipToOrg &&
              relationshipQueryType?.getRelationshipType && (
                <>
                  <SubTitle>Relationship to organisation</SubTitle>
                  <FormInput
                    variant={FormInputVariant.DROPDOWN}
                    options={
                      relationshipQueryType?.getRelationshipType?.map(
                        (relationship) => ({
                          label: relationship.name,
                          value: relationship.id,
                        }),
                      ) ?? []
                    }
                    onChange={(e) =>
                      setRelationshipToCompany({
                        label: String((e as DefaultOption)?.label),
                        value: Number((e as DefaultOption)?.value),
                      })
                    }
                    value={relationshipToCompany}
                    data-testid="report-relationship-to-organisation-type-field"
                  />
                </>
              )}
          </>
        ) : (
          <ExteralReporterContainer>
            <SubTitle>{subTitle}</SubTitle>
            <TextBox disabled={!!currentReport?.User?.eid}>
              <TextInput
                data-testid={`external-${props['data-testid']}-name`}
                disabled={!!currentReport?.User?.eid}
                placeholder={`Enter Reporter name (optional)`}
                value={name}
                onChange={(event) => setName(event.target.value)}
              ></TextInput>
            </TextBox>
            <SubTitle>Alternate email (not company email)</SubTitle>
            <TextBox disabled={!!currentReport?.User?.eid}>
              <TextInput
                data-testid={`external-${props['data-testid']}-email`}
                disabled={!!currentReport?.User?.eid}
                placeholder={'Enter reporter email (required)'}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              ></TextInput>
            </TextBox>
            {relationshipQueryType?.getRelationshipType && (
              <>
                <SubTitle>Relationship to organisation</SubTitle>
                <FormInput
                  variant={FormInputVariant.DROPDOWN}
                  options={
                    relationshipQueryType?.getRelationshipType?.map(
                      (relationship) => ({
                        label: relationship.name,
                        value: relationship.id,
                      }),
                    ) ?? []
                  }
                  onChange={(e) =>
                    setRelationshipToCompany({
                      label: String((e as DefaultOption)?.label),
                      value: Number((e as DefaultOption)?.value),
                    })
                  }
                  value={relationshipToCompany}
                  data-testid="report-relationship-to-organisation-type-field"
                />
              </>
            )}
            {currentReport?.User?.eid ? (
              <ExplainerText data-testid="external-reporter-warning">
                You cannot add name and alternate email because you&apos;ve
                previously added an employee in the reporter details. To create
                a report using an alternate email, please create a new report.
              </ExplainerText>
            ) : (
              <ExplainerText>
                If the reporter would like to remain anonymous, please use an
                alternate email here (non-identifiable). If you add a name you
                must add an email.
              </ExplainerText>
            )}
          </ExteralReporterContainer>
        )}
      </Body>

      <Footer>
        {!showOnlyCM && (
          <AnonymousContainer>
            <Checkbox
              checked={currentReport?.anonymous ?? false}
              onChange={setAnonymous}
              data-testid={`${props['data-testid']}-modal-anonymous-checkbox`}
            />
            <AnonymousText>Mark reporter as anonymous</AnonymousText>
          </AnonymousContainer>
        )}
        <Button
          data-testid={`save-${props['data-testid']}`}
          onClick={() =>
            updateData({
              internalUser,
              selectedUser,
              email,
              name,
              relationshipToCompany: selectedRelationshipToCompany,
            })
          }
          disabled={addUserDisabled()}
          variant="primary"
          size="small"
        >
          <StyledIconInButton
            size="small"
            position="left"
            className="uil uil-plus"
          />
          {buttonText}
        </Button>
      </Footer>
    </Modal>
  );
};
